import React, { useEffect, useState } from 'react';
import { Paper, Grid, Typography, Box, TableBody, TableCell, TableHead, TableRow, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import BlockIcon from '@mui/icons-material/Block';
import BuildIcon from '@mui/icons-material/Build';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import axios from '../../api/client';
import StatCard from '../StatCard';
import EmptyState from '../EmptyState';
import ScrollTable, { EMBEDDED_MAX_HEIGHT } from '../ScrollTable';


const PropertyPortfolioDashboard = () => {
  const [properties, setProperties] = useState([]);
  const [landlords, setLandlords] = useState([]);
  // Only the setter is used: the dashboard renders its figures as they arrive rather
  // than gating on a spinner.
  const [, setLoading] = useState(false);
  const [expandedLandlord, setExpandedLandlord] = useState(null);

  useEffect(() => {
    fetchProps();
    fetchLandlords();
  }, []);

  const fetchProps = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/properties');
      setProperties(res.data || []);
    } catch {}
    setLoading(false);
  };

  const fetchLandlords = async () => {
    try {
      const res = await axios.get('/landlords');
      setLandlords(res.data || []);
    } catch (error) {
      console.error('Error fetching landlords:', error);
    }
  };

  // Group properties by landlord
  const propertiesByLandlord = landlords.map(landlord => {
    const landlordProperties = properties.filter(p => 
      p.landlord && (p.landlord._id === landlord._id || p.landlord === landlord._id)
    );
    return {
      landlord,
      properties: landlordProperties
    };
  });

  // Overall summaries
  const num = properties.length;
  const occupied = properties.filter(p=>p.status==='occupied').length;
  const available = properties.filter(p=>p.status==='available').length;
  const unavailable = properties.filter(p=>p.status==='unavailable').length;
  const maintenance = properties.filter(p=>p.status==='maintenance').length;
  
  // Calculate rent roll from units and allocations
  const totalUnits = properties.reduce((sum, p) => sum + (p.unitsCount || 0), 0);
  const occupiedUnits = properties.reduce((sum, p) => sum + (p.occupiedUnitsCount || 0), 0);
  const totalSquareFeet = properties
    .filter(p => p.propertyType === 'commercial')
    .reduce((sum, p) => sum + (p.totalSquareFeet || p.squareFeet || 0), 0);
  const handleAccordionChange = (landlordId) => (event, isExpanded) => {
    setExpandedLandlord(isExpanded ? landlordId : null);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight:700 }}>Portfolio Dashboard</Typography>
      
      {/* Overall Summary Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={3}><StatCard label="Total Properties" value={num} tone="neutral" icon={<ApartmentIcon fontSize="small" />} /></Grid>
        <Grid item xs={6} sm={4} md={3}><StatCard label="Occupied" value={occupied} tone="positive" icon={<CheckCircleIcon fontSize="small" />} /></Grid>
        <Grid item xs={6} sm={4} md={3}><StatCard label="Available" value={available} tone="neutral" icon={<EventAvailableIcon fontSize="small" />} /></Grid>
        <Grid item xs={6} sm={4} md={3}><StatCard label="Unavailable" value={unavailable} tone="critical" icon={<BlockIcon fontSize="small" />} /></Grid>
        <Grid item xs={6} sm={4} md={3}><StatCard label="Maintenance" value={maintenance} tone="caution" icon={<BuildIcon fontSize="small" />} /></Grid>
        <Grid item xs={6} sm={4} md={3}><StatCard label="Total Units" value={totalUnits} tone="info" icon={<MeetingRoomIcon fontSize="small" />} /></Grid>
        <Grid item xs={6} sm={4} md={3}><StatCard label="Occupied Units" value={occupiedUnits} tone="info" icon={<MeetingRoomIcon fontSize="small" />} /></Grid>
        <Grid item xs={6} sm={4} md={3}><StatCard label="Commercial Sq Ft" value={totalSquareFeet.toLocaleString()} tone="info" icon={<SquareFootIcon fontSize="small" />} /></Grid>
      </Grid>

      {/* Landlord-based Portfolio View */}
      <Typography variant="h5" sx={{ mb: 2, mt: 3 }}>Portfolio by Landlord</Typography>
      {propertiesByLandlord.map(({ landlord, properties: landlordProps }) => {
        const landlordUnits = landlordProps
          .filter(p => p.propertyType !== 'commercial')
          .reduce((sum, p) => sum + (p.unitsCount || 0), 0);
        const landlordOccupiedUnits = landlordProps
          .filter(p => p.propertyType !== 'commercial')
          .reduce((sum, p) => sum + (p.occupiedUnitsCount || 0), 0);
        const landlordCommercialSqFt = landlordProps
          .filter(p => p.propertyType === 'commercial')
          .reduce((sum, p) => sum + (p.totalSquareFeet || p.squareFeet || 0), 0);
        const landlordAllocatedSqFt = landlordProps
          .filter(p => p.propertyType === 'commercial')
          .reduce((sum, p) => sum + (p.allocatedSquareFeet || 0), 0);
        const landlordOccupied = landlordProps.filter(p => p.status === 'occupied').length;
        const landlordAvailable = landlordProps.filter(p => p.status === 'available').length;
        
        return (
          <Accordion
            key={landlord._id}
            expanded={expandedLandlord === landlord._id}
            onChange={handleAccordionChange(landlord._id)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" width="100%">
                {landlord.type === 'company' ? (
                  <BusinessIcon color="primary" sx={{ mr: 2 }} />
                ) : (
                  <PersonIcon color="primary" sx={{ mr: 2 }} />
                )}
                <Box flex={1}>
                  <Typography variant="h6">{landlord.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {landlordProps.length} properties • {landlordUnits} units • {landlordCommercialSqFt.toLocaleString()} sq ft commercial
                  </Typography>
                </Box>
                <Chip
                  label={landlord.isActive ? 'Active' : 'Inactive'}
                  color={landlord.isActive ? 'success' : 'default'}
                  size="small"
                  sx={{ mr: 2 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6} md={2.4}>
                  <StatCard label="Properties" value={landlordProps.length} tone="neutral" />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <StatCard label="Occupied" value={landlordOccupied} tone="positive" />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <StatCard label="Available" value={landlordAvailable} tone="neutral" />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <StatCard label="Units" value={landlordUnits} caption={`${landlordOccupiedUnits} occupied`} tone="info" />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                  <StatCard
                    label="Commercial Sq Ft"
                    value={landlordCommercialSqFt.toLocaleString()}
                    caption={`${landlordAllocatedSqFt.toLocaleString()} allocated`}
                    tone="info"
                  />
                </Grid>
              </Grid>
              <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Property Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Tenants</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {landlordProps.map((p) => (
                      <TableRow key={p._id}>
                        <TableCell>{p.propertyName}</TableCell>
                        <TableCell>
                          <Chip
                            label={p.propertyType === 'commercial' ? 'Commercial' : 'Residential'}
                            color={p.propertyType === 'commercial' ? 'secondary' : 'primary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={p.status}
                            color={
                              p.status === 'occupied'
                                ? 'success'
                                : p.status === 'available'
                                ? 'primary'
                                : p.status === 'unavailable'
                                ? 'error'
                                : 'warning'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {p.propertyType === 'commercial' ? (
                            <Typography variant="body2">
                              {p.totalSquareFeet || p.squareFeet || 0} sq ft
                              {p.allocatedSquareFeet !== undefined && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {p.allocatedSquareFeet} allocated
                                </Typography>
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              {p.unitsCount || p.numberOfUnits || 0} units
                              {p.occupiedUnitsCount !== undefined && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {p.occupiedUnitsCount} occupied
                                </Typography>
                              )}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {p.propertyType === 'commercial' ? (
                            <Typography variant="body2">
                              {p.allocationsCount || 0} allocation(s)
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              {p.occupiedUnitsCount || 0} tenant(s)
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {landlordProps.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}><EmptyState compact title="No properties for this landlord" /></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </ScrollTable>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {propertiesByLandlord.length === 0 && (
        <Paper variant="outlined">
          <EmptyState
            icon={<BusinessIcon sx={{ fontSize: 44 }} />}
            title="No landlords yet"
            description="Add a landlord to start building out your property portfolio."
          />
        </Paper>
      )}

      {/* Properties Summary Section */}
      <Typography variant="h5" sx={{ mb: 2, mt: 3 }}>Properties Summary</Typography>
      <Paper sx={{ p: 2 }}>
        <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Landlord</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.slice(0, 10).map(p => {
                const landlord = landlords.find(l => 
                  p.landlord && (p.landlord._id === l._id || p.landlord === l._id)
                );
                return (
                  <TableRow key={p._id}>
                    <TableCell>{p.propertyName}</TableCell>
                    <TableCell>{landlord?.name || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={p.propertyType === 'commercial' ? 'Commercial' : 'Residential'}
                        color={p.propertyType === 'commercial' ? 'secondary' : 'primary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{p.addressStreet}</TableCell>
                    <TableCell>
                      {p.propertyType === 'commercial' ? (
                        `${p.totalSquareFeet || p.squareFeet || 0} sq ft`
                      ) : (
                        `${p.unitsCount || p.numberOfUnits || 0} units`
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {properties.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}><EmptyState compact title="No properties yet" /></TableCell>
                </TableRow>
              )}
            </TableBody>
          </ScrollTable>
      </Paper>
    </Box>
  );
};

export default PropertyPortfolioDashboard;
